import { Component, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';

import { CommonService } from '../../../service/common.service';
import {
  Machine_,
  ParameterLabel,
  RecommendationLabel,
  ToolContainer,
  Recommendation_
} from '../../../service/formulas/objects';
import { Formulas } from '../../../service/formulas/formulas';
import { Utils } from '../../../service/utils';
import math from 'mathjs';

@Component({
  selector: 'section-summary',
  templateUrl: 'summary.html',
  styleUrls: ['summary.scss']
})
export class Summary {
  private subscription: Subscription;
  public summaryFields: SummaryField[];
  @Output() goTab: EventEmitter<any> = new EventEmitter<any>();
  @Output() resetAll: EventEmitter<any> = new EventEmitter<any>();
  public settingsHolder: ToolContainer;
  constructor(private commonService: CommonService, private utils: Utils, private formulas: Formulas) {
    this.summaryFields = this.setDefaultFields();
  }

  ngAfterViewInit() {
    this.subscription = this.commonService.notifyObservable$.subscribe(res => {
      if (res.hasOwnProperty('option')) {
        if (res.option === 'logout') {
          this.subscription.unsubscribe();
        } else if (res.option === 'summaryAlert') {
          let settingsHolder: ToolContainer = res.value['settingsHolder'];
          let recommendation: Recommendation_ = res.value['recommendation'];
          if (settingsHolder && settingsHolder.parameters && recommendation && settingsHolder.machine) {
            if (
              settingsHolder.mapMode === 2 &&
              settingsHolder.machine.holderAdjustment &&
              settingsHolder.machine.spindleAdjustment &&
              settingsHolder.mapModeTwoValues
            ) {
              this.summaryFields = this.setMapeModeTwoFields(
                settingsHolder.mapModeTwoValues.recommendationLabels,
                settingsHolder.mapModeTwoValues.parameterLabels,
                settingsHolder.machine,
                recommendation.speedAggression,
                recommendation.feedAggression
              );
            } else {
              const toolPath = settingsHolder.currenttoolpath.operationName.toLowerCase();

              if (toolPath === '3d surfacing') {
                this.summaryFields = this.set3DSurfacingFields(
                  recommendation['rpm'],
                  recommendation['ipm'],
                  recommendation['ipt'],
                  settingsHolder.parameters.adocdecimal,
                  settingsHolder.parameters.rdocdecimal
                );
              } else if (toolPath === 'chamfering') {
                this.summaryFields = this.setChamferingFields(
                  recommendation['rpm'],
                  recommendation['ipm'],
                  recommendation['ipt'],
                  settingsHolder.parameters.chamferHeight,
                  settingsHolder.parameters.chamferWidth
                );
              } else if (toolPath === 'trochoidal slotting') {
                this.summaryFields = this.setTrochoidalSlotting(
                  recommendation['rpm'],
                  recommendation['ipmFeedInsideSlot'],
                  recommendation['trochoidalSlotWidth'],
                  settingsHolder.parameters.adocdecimal,
                  settingsHolder.parameters.rdocdecimal,
                  recommendation['mrr']
                );
              } else {
                this.summaryFields = this.setDefaultFields(
                  recommendation['rpm'],
                  recommendation['ipm'],
                  recommendation['ipt'],
                  settingsHolder.parameters.adocdecimal,
                  settingsHolder.parameters.rdocdecimal,
                  recommendation['mrr']
                );
              }
            }
          } else {
            this.summaryFields = this.setDefaultFields();
          }
        }
      }
    });
  }

  setDefaultFields = (
    rpm: number = 0,
    ipm: number = 0,
    ipt: number = 0,
    adoc: number = 0,
    rdoc: number = 0,
    mrr: number = 0
  ): SummaryField[] => {
    return [
      {
        name: 'SPEED',
        value: rpm,
        numberFormat: '.0-0',
        tabIndex: 6
      },
      {
        name: 'FEED PER MINUTE',
        value: ipm,
        numberFormat: '.0-0',
        tabIndex: 6
      },
      {
        name: 'FEED PER TOOTH',
        value: ipt,
        numberFormat: '.4-4',
        tabIndex: 6
      },
      {
        name: 'ADOC',
        value: adoc,
        numberFormat: null,
        tabIndex: 5
      },
      {
        name: 'RDOC',
        value: rdoc,
        numberFormat: null,
        tabIndex: 5
      },
      {
        name: 'MRR',
        value: mrr,
        numberFormat: '.0-2',
        tabIndex: 6
      }
    ];
  };

  set3DSurfacingFields = (
    rpm: number = 0,
    ipm: number = 0,
    ipt: number = 0,
    adoc: number = 0,
    rdoc: number = 0
  ): SummaryField[] => {
    return [
      {
        name: 'SPEED',
        value: rpm,
        numberFormat: '.0-0',
        tabIndex: 6
      },
      {
        name: 'FEED PER MINUTE',
        value: ipm,
        numberFormat: '.0-0',
        tabIndex: 6
      },
      {
        name: 'FEED PER TOOTH',
        value: ipt,
        numberFormat: '.4-4',
        tabIndex: 6
      },
      {
        name: 'ADOC',
        value: adoc,
        numberFormat: null,
        tabIndex: 5
      },
      {
        name: 'RDOC',
        value: rdoc,
        numberFormat: null,
        tabIndex: 5
      }
    ];
  };

  setChamferingFields = (
    rpm: number = 0,
    ipm: number = 0,
    ipt: number = 0,
    chamferHeight: number = 0,
    chamferWidth: number = 0
  ): SummaryField[] => {
    return [
      {
        name: 'SPEED',
        value: rpm,
        numberFormat: '.0-0',
        tabIndex: 6
      },
      {
        name: 'FEED PER MINUTE',
        value: ipm,
        numberFormat: '.0-0',
        tabIndex: 6
      },
      {
        name: 'FEED PER TOOTH',
        value: ipt,
        numberFormat: '.4-4',
        tabIndex: 6
      },
      {
        name: 'CHAMFER HEIGHT',
        value: chamferHeight,
        numberFormat: null,
        tabIndex: 5
      },
      {
        name: 'CHAMFER WIDTH',
        value: chamferWidth,
        numberFormat: null,
        tabIndex: 5
      }
    ];
  };

  setTrochoidalSlotting = (
    rpm: number = 0,
    ipmFeedInsideSlot: number = 0,
    trochoidalSlotWidth: number = 0,
    adoc: number = 0,
    rdoc: number = 0,
    mrr: number = 0
  ): SummaryField[] => {
    return [
      {
        name: 'SPEED',
        value: rpm,
        numberFormat: '.0-0',
        tabIndex: 6
      },
      {
        name: 'FEED PER MINUTE',
        value: ipmFeedInsideSlot,
        numberFormat: '.0-2',
        tabIndex: 6
      },
      {
        name: 'TROCHOIDAL SLOT WIDTH',
        value: trochoidalSlotWidth,
        numberFormat: '.4-4',
        tabIndex: 6
      },
      {
        name: 'ADOC',
        value: adoc,
        numberFormat: null,
        tabIndex: 5
      },
      {
        name: 'RDOC',
        value: rdoc,
        numberFormat: null,
        tabIndex: 5
      },
      {
        name: 'MRR',
        value: mrr,
        numberFormat: '.0-2',
        tabIndex: 6
      }
    ];
  };

  setMapeModeTwoFields = (
    recommendations: RecommendationLabel[],
    parameters: ParameterLabel[],
    machine: Machine_,
    speedAggression: number,
    feedAggression: number
  ): SummaryField[] => {
    const summeryFields: SummaryField[] = [];
    const selectedHolderAdjustment = machine.holderAdjustment.find(ha => ha.adjust === 'SFM');
    const selectedSpindleAdjustment = machine.spindleAdjustment.find(sa => sa.adjust === 'FPT');

    const sfmRation = this.formulas.calculateSfmRatio(
      recommendations,
      selectedHolderAdjustment.factor,
      selectedSpindleAdjustment.factor,
      machine.maxRPM,
      speedAggression,
      feedAggression
    );
    const fptRation = this.formulas.calculateFptRatio(
      recommendations,
      selectedHolderAdjustment.factor,
      selectedSpindleAdjustment.factor,
      machine.maxIPM,
      speedAggression,
      feedAggression,
      sfmRation
    );

    for (let i = 1; i < 5 && i < recommendations.length; i++) {
      const currentRec = recommendations[i];
      summeryFields.push({
        name: currentRec.recommendationLabel,
        value: this.calculateRecommendationValue(
          currentRec,
          selectedHolderAdjustment.factor,
          selectedSpindleAdjustment.factor,
          speedAggression,
          feedAggression,
          sfmRation,
          fptRation
        ),
        numberFormat: '.0-4',
        tabIndex: 6
      });
    }
    for (let i = 0; i < 2 && i < parameters.length; i++) {
      const currentParam = parameters[i];
      summeryFields.push({
        name: currentParam.parameterLabel,
        value: Number(currentParam.parameterValue),
        numberFormat: '.0-4',
        tabIndex: 5
      });
    }
    return summeryFields;
  };

  private calculateRecommendationValue = (
    recommendation: RecommendationLabel,
    holderFactor: number,
    spindleFactor: number,
    speedAggression: number,
    feedAggression: number,
    sfmRation: number,
    fptRation: number
  ): number => {
    const recValue =
      Number(recommendation.recommendationValue) *
      (recommendation.recommendationAdjustment.includes('SFM') ? holderFactor * speedAggression * sfmRation : 1) *
      (recommendation.recommendationAdjustment.includes('FPT') ? spindleFactor * feedAggression * fptRation : 1);

    return math.round(recValue, Number(recommendation.recommendationPrecision));
  };

  goToTab = (selectedIndex: number) => {
    this.goTab.emit(selectedIndex);
  };

  resetSetup = () => {
    this.resetAll.emit();
    this.utils.apptentiveEngage('Reset Application Button');
  };
}

interface SummaryField {
  name: string;
  value: number;
  numberFormat: string;
  tabIndex: number;
}
